/**
 * The groups table's first row (19/10/2026): every campaign group's
 * auto-send at once, with the same states as one group's row — Off ·
 * Simulated · Live (Live asks first). When the groups differ, no state is
 * picked and the row says so. Shown with two groups or more.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenAutoSendMode, DropGenGroupSendStatus } from '@trafficpulse/shared';

import { SegmentedPills } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { modeOptions, useModeRights } from '../groups/mode-options';
import { AllGroupsConfirmDialog } from './AllGroupsConfirmDialog';
import { commonMode, useAllGroupsSwitch } from './use-all-groups-switch';

const SAID: Record<DropGenAutoSendMode, string> = {
  off: 'every group is off — you send',
  shadow: 'every group is Simulated — they say what they would have sent',
  on: 'every group is Live — each draft goes out at its time',
};

export const AllGroupsRow: FC<{ plan: DropGenPlanDetail; groups: DropGenGroupSendStatus[] }> = ({ plan, groups }) => {
  const sw = useAllGroupsSwitch(plan.countryKey, groups);
  const common = commonMode(groups);
  const options = modeOptions(useModeRights(), common);
  if (groups.length < 2) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/60 bg-muted/20 px-3 py-2.5" data-testid="autosend-all-groups">
      <span className={sw.canSwitch ? '' : 'pointer-events-none opacity-50'} aria-label="Auto-send for every campaign group">
        <SegmentedPills<DropGenAutoSendMode | 'mixed'>
          options={options}
          value={common ?? 'mixed'}
          onChange={(next) => (next === 'mixed' ? undefined : sw.pick(next))}
          className="[&_button]:h-6 [&_button]:px-2 [&_button]:text-[11px]"
        />
      </span>
      <span className="font-semibold">All campaign groups</span>
      <span className="text-muted-foreground">{common ? SAID[common] : 'mixed — pick one to set every group'}</span>
      <AllGroupsConfirmDialog open={sw.confirming} groups={groups} mode={sw.confirmMode} autoGenerateOff={!plan.autoGenerate} busy={sw.busy} onConfirm={sw.confirm} onCancel={sw.cancel} />
    </div>
  );
};
